import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import {
    FaCar,
    FaCheckCircle,
    FaExclamationTriangle,
    FaExternalLinkAlt,
    FaQuestionCircle,
    FaSpinner,
    FaTimes,
    FaChevronDown,
    FaChevronUp,
    FaSearch,
    FaImage,
} from 'react-icons/fa';
import { storeProductUrl } from '../../../utils/storeConstants';
import CompatibilityVehicleForm, {
    emptyVehicleValues,
    isValidVin,
} from './CompatibilityVehicleForm';

const isSelfRefSource = (s) => {
    const hay = `${s?.url || ''} ${s?.title || ''} ${s?.description || ''}`.toLowerCase();
    return /osoriorepuestos|osorio network|osorionetwork/.test(hay);
};

/**
 * Modal de prueba interna: misma UX/UI de tienda, endpoint admin sin cuota.
 */
const CompatibilityCheckModal = ({
    isOpen,
    onClose,
    token,
    producto,
    productosHijos = [],
}) => {
    const titleId = useId();
    const dialogRef = useRef(null);
    const [vehicle, setVehicle] = useState(emptyVehicleValues());
    const [uiState, setUiState] = useState('form');
    const [errorMsg, setErrorMsg] = useState('');
    const [payload, setPayload] = useState(null);
    const [selectedVariantId, setSelectedVariantId] = useState('');
    const [showTech, setShowTech] = useState(false);
    const [altState, setAltState] = useState('idle');
    const [alternative, setAlternative] = useState(null);
    const [altError, setAltError] = useState('');
    const [loadingLongWait, setLoadingLongWait] = useState(false);

    const isParent = Boolean(producto?.es_padre);
    const selectedVariant = useMemo(
        () => productosHijos.find((h) => String(h.id_producto) === String(selectedVariantId)) || null,
        [productosHijos, selectedVariantId]
    );

    const targetProduct = isParent ? selectedVariant : producto;
    const itemId = targetProduct?.id_producto;
    const itemName = targetProduct
        ? `${targetProduct.sku || ''} - ${targetProduct.nombre_producto || targetProduct.marca_hijo || targetProduct.nombre_variante || producto?.nombre_producto || ''}`.trim()
        : producto?.nombre_producto || '';

    useEffect(() => {
        if (!isOpen) return undefined;
        setVehicle(emptyVehicleValues());
        setUiState('form');
        setErrorMsg('');
        setPayload(null);
        setShowTech(false);
        setSelectedVariantId('');
        setAltState('idle');
        setAlternative(null);
        setAltError('');
        setLoadingLongWait(false);

        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = prevOverflow;
        };
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) return undefined;
        const onKey = (e) => {
            if (e.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [isOpen, onClose]);

    useEffect(() => {
        if (!isOpen || !dialogRef.current) return;
        const focusable = dialogRef.current.querySelector(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        focusable?.focus();
    }, [isOpen, uiState]);

    useEffect(() => {
        if (uiState !== 'loading') {
            setLoadingLongWait(false);
            return undefined;
        }
        setLoadingLongWait(false);
        const timer = setTimeout(() => setLoadingLongWait(true), 10_000);
        return () => clearTimeout(timer);
    }, [uiState]);

    if (!isOpen) return null;

    const canSubmit =
        Boolean(itemId) &&
        String(vehicle.vehicleQuery || '').trim().length >= 8 &&
        isValidVin(vehicle.vin);

    const handleSubmit = async () => {
        if (!canSubmit || !itemId) return;
        if (isParent && !selectedVariant) {
            setErrorMsg('Selecciona una variante del producto para validar compatibilidad.');
            setUiState('error');
            return;
        }

        setUiState('loading');
        setErrorMsg('');
        setAltState('idle');
        setAlternative(null);
        setAltError('');

        try {
            const { data } = await axios.post(
                '/api/ecommerce/compatibility/check-admin',
                {
                    productId: itemId,
                    vehicle: {
                        vehicleQuery: vehicle.vehicleQuery.trim(),
                        engine: vehicle.engine.trim() || undefined,
                        trim: vehicle.trim.trim() || undefined,
                        fuelType: vehicle.fuelType || undefined,
                        vin: vehicle.vin.trim() || undefined,
                        notes: vehicle.notes.trim() || undefined,
                    },
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (data?.success === false) {
                setErrorMsg(data.message || 'No se pudo validar la compatibilidad.');
                setUiState('error');
                return;
            }

            setPayload(data);
            setUiState('result');
        } catch (err) {
            const data = err?.response?.data;
            setErrorMsg(data?.message || 'Error de red al validar compatibilidad.');
            setUiState('error');
        }
    };

    const handleSearchAlternative = async () => {
        if (!itemId) return;
        setAltState('loading');
        setAltError('');
        setAlternative(null);
        try {
            const { data } = await axios.post(
                '/api/ecommerce/compatibility/alternatives-admin',
                {
                    productId: itemId,
                    productName:
                        targetProduct?.nombre_producto ||
                        targetProduct?.marca_hijo ||
                        producto?.nombre_producto,
                    vehicle: {
                        vehicleQuery: vehicle.vehicleQuery.trim(),
                        engine: vehicle.engine.trim() || undefined,
                        trim: vehicle.trim.trim() || undefined,
                    },
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (data?.success && data?.alternative) {
                setAlternative(data.alternative);
                setAltState('found');
            } else {
                setAltState('none');
            }
        } catch (err) {
            setAltError(err?.response?.data?.message || 'No se pudo buscar alternativa.');
            setAltState('error');
        }
    };

    const result = payload?.result;
    const status = result?.status;
    const storeId = itemId || producto?.id_producto;
    const safeSources = Array.isArray(result?.sources)
        ? result.sources.filter((s) => !isSelfRefSource(s))
        : [];
    const productLabel =
        targetProduct?.nombre_producto ||
        targetProduct?.marca_hijo ||
        producto?.nombre_producto ||
        'este repuesto';
    const productImages =
        (Array.isArray(targetProduct?.imagenes) && targetProduct.imagenes.length
            ? targetProduct.imagenes
            : producto?.imagenes) || [];
    const productImageUrl = productImages[0]?.url || null;
    const searchLabel = `Buscar ${productLabel} para ${String(vehicle.vehicleQuery || '').trim() || 'el vehículo'}`;

    const modal = (
        <div className="fixed inset-0 z-[100000] flex items-end sm:items-center justify-center p-0 sm:p-4">
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
            <div
                ref={dialogRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                className="relative w-full sm:max-w-xl bg-white rounded-t-3xl sm:rounded-2xl shadow-2xl max-h-[92vh] flex flex-col overflow-hidden"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex-shrink-0 sticky top-0 z-10 border-b border-indigo-100 bg-indigo-50 px-5 py-4 flex items-center justify-between">
                    <div className="min-w-0 pr-3">
                        <h3
                            id={titleId}
                            className="font-black text-gray-900 uppercase tracking-tight text-sm sm:text-base flex items-center gap-2"
                        >
                            <FaCar className="text-indigo-600" /> Probar compatibilidad con IA
                        </h3>
                        <p className="text-xs text-gray-600 mt-0.5 truncate">
                            {itemName || producto?.nombre_producto}
                        </p>
                        <p className="text-[11px] text-indigo-700 mt-0.5 font-semibold">
                            Endpoint admin · sin cuota · Gemini: uso interno
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="p-2 rounded-full hover:bg-white/80 text-gray-500 shrink-0"
                        aria-label="Cerrar"
                    >
                        <FaTimes />
                    </button>
                </div>

                <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-5 py-4 space-y-4">
                    <div className="rounded-xl border border-gray-100 bg-gray-50/40 p-3">
                        <p className="mb-2 text-[10px] font-bold uppercase tracking-wide text-yellow-800">
                            Estás consultando sobre este producto
                        </p>
                        <div className="flex items-center gap-3">
                            <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-gray-100 bg-white shadow-sm">
                                {productImageUrl ? (
                                    <img
                                        src={productImageUrl}
                                        alt={productLabel}
                                        className="h-full w-full object-contain p-1"
                                    />
                                ) : (
                                    <span
                                        className="inline-flex rounded-full bg-yellow-100 p-2 text-yellow-500"
                                        aria-label="Sin imagen disponible"
                                    >
                                        <FaImage />
                                    </span>
                                )}
                            </div>
                            <div className="min-w-0">
                                <p className="line-clamp-2 text-xs font-bold uppercase leading-tight text-gray-900">
                                    {productLabel}
                                </p>
                                {targetProduct?.sku && (
                                    <p className="mt-1 text-[10px] font-medium text-gray-400">
                                        SKU: {targetProduct.sku}
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>

                    {uiState === 'form' && (
                        <>
                            <p className="text-sm text-gray-600">
                                Escribe el carro en texto libre (como se lo dirías a Google). El catálogo
                                interno es solo una pista; la evidencia web/OEM pesa más.
                            </p>

                            {isParent && (
                                <label className="block text-sm font-semibold text-gray-700">
                                    Variante a validar *
                                    <select
                                        className="mt-1 block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                        value={selectedVariantId}
                                        onChange={(e) => setSelectedVariantId(e.target.value)}
                                    >
                                        <option value="">Selecciona una variante</option>
                                        {productosHijos.map((h) => (
                                            <option key={h.id_producto} value={h.id_producto}>
                                                {h.sku || h.id_producto}
                                                {h.marca_hijo || h.nombre_variante
                                                    ? ` — ${h.marca_hijo || h.nombre_variante}`
                                                    : ''}
                                                {h.existencias != null ? ` · stock ${h.existencias}` : ''}
                                            </option>
                                        ))}
                                    </select>
                                    {!productosHijos.length && (
                                        <span className="mt-1 block text-xs text-amber-700">
                                            No hay variantes cargadas para este padre.
                                        </span>
                                    )}
                                </label>
                            )}

                            <CompatibilityVehicleForm values={vehicle} onChange={setVehicle} />
                        </>
                    )}

                    {uiState === 'loading' && (
                        <div className="py-12 text-center space-y-3">
                            <FaSpinner className="mx-auto text-3xl text-indigo-600 animate-spin" />
                            <p className="font-bold text-gray-900">
                                {loadingLongWait
                                    ? 'Siguiendo con fuentes técnicas…'
                                    : 'Consultando catálogo y fuentes técnicas…'}
                            </p>
                            <p className="text-sm text-gray-500">
                                {loadingLongWait
                                    ? 'La búsqueda web puede tardar un poco más en algunos casos.'
                                    : 'Esto puede tomar entre 5 y 20 segundos.'}
                            </p>
                        </div>
                    )}

                    {uiState === 'error' && (
                        <div className="py-8 text-center space-y-3">
                            <FaExclamationTriangle className="mx-auto text-3xl text-red-500" />
                            <p className="font-bold text-gray-900">No pudimos validar ahora</p>
                            <p className="text-sm text-gray-600">{errorMsg}</p>
                        </div>
                    )}

                    {uiState === 'result' && result && (
                        <div className="space-y-4">
                            <div
                                className={`rounded-2xl border p-4 ${
                                    status === 'compatible'
                                        ? 'bg-green-50 border-green-200'
                                        : status === 'incompatible'
                                          ? 'bg-red-50 border-red-200'
                                          : 'bg-amber-50 border-amber-200'
                                }`}
                            >
                                <div className="flex items-start gap-3">
                                    {status === 'compatible' && (
                                        <FaCheckCircle className="text-2xl text-green-600 mt-0.5" />
                                    )}
                                    {status === 'incompatible' && (
                                        <FaExclamationTriangle className="text-2xl text-red-600 mt-0.5" />
                                    )}
                                    {status === 'unknown' && (
                                        <FaQuestionCircle className="text-2xl text-amber-600 mt-0.5" />
                                    )}
                                    <div>
                                        <p className="font-black text-gray-900 uppercase text-sm">
                                            {status === 'compatible' && 'Compatible'}
                                            {status === 'incompatible' && 'Incompatible'}
                                            {status === 'unknown' && 'No concluyente'}
                                        </p>
                                        <p className="text-sm text-gray-700 mt-1 leading-relaxed">
                                            {result.summary}
                                        </p>
                                        {result.confidence != null && (
                                            <p className="text-xs text-gray-500 mt-2">
                                                Confianza: {Math.round(Number(result.confidence) * 100)}%
                                                {payload?.cached ? ' · resultado en caché' : ''}
                                            </p>
                                        )}
                                        {payload?.geminiKeyChannel === 'internal' && (
                                            <p className="mt-2 inline-flex items-center rounded-md bg-indigo-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-indigo-800">
                                                Gemini: uso interno
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {safeSources.length > 0 && (
                                <div>
                                    <p className="text-xs font-bold uppercase tracking-wide text-gray-400 mb-2">
                                        Fuentes
                                    </p>
                                    <ul className="space-y-1.5">
                                        {safeSources.slice(0, 5).map((s, i) => (
                                            <li key={i} className="text-sm">
                                                {s.url ? (
                                                    <a
                                                        href={s.url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="text-indigo-700 hover:underline font-medium"
                                                    >
                                                        {s.title || 'Fuente técnica'}
                                                    </a>
                                                ) : (
                                                    <span className="text-gray-700">
                                                        {s.title || s.description}
                                                    </span>
                                                )}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            <p className="text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded-xl px-3 py-2">
                                Revisa motor y versión antes de comprar. La IA puede equivocarse ante datos
                                incompletos.
                            </p>

                            {status === 'incompatible' && (
                                <div className="space-y-3">
                                    {altState === 'idle' && (
                                        <button
                                            type="button"
                                            onClick={handleSearchAlternative}
                                            className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-3 rounded-xl inline-flex items-center justify-center gap-2"
                                        >
                                            <FaSearch /> {searchLabel}
                                        </button>
                                    )}
                                    {altState === 'loading' && (
                                        <div className="py-6 text-center space-y-2">
                                            <FaSpinner className="mx-auto text-2xl text-indigo-600 animate-spin" />
                                            <p className="text-sm text-gray-600">Buscando alternativa…</p>
                                        </div>
                                    )}
                                    {altState === 'found' && alternative && (
                                        <div className="rounded-2xl border border-gray-200 p-4 space-y-2">
                                            <p className="text-xs font-bold uppercase tracking-wide text-gray-400">
                                                Alternativa sugerida (catálogo)
                                            </p>
                                            <p className="font-bold text-gray-900 text-sm">{alternative.nombre}</p>
                                            <p className="text-xs text-gray-500">
                                                {[alternative.marca, alternative.sku, `stock ${alternative.stock}`]
                                                    .filter(Boolean)
                                                    .join(' · ')}
                                            </p>
                                            <a
                                                href={alternative.url || storeProductUrl(alternative.id)}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="inline-flex items-center gap-2 text-sm font-semibold text-indigo-700 hover:underline"
                                            >
                                                <FaExternalLinkAlt /> Ver en tienda
                                            </a>
                                        </div>
                                    )}
                                    {(altState === 'none' || altState === 'error') && (
                                        <p className="text-sm text-amber-800 bg-amber-50 border border-amber-100 rounded-xl px-3 py-2">
                                            {altError || 'Sin alternativa clara en catálogo para ese vehículo.'}
                                        </p>
                                    )}
                                </div>
                            )}

                            <button
                                type="button"
                                onClick={() => setShowTech((v) => !v)}
                                className="w-full flex items-center justify-between text-left text-xs font-bold uppercase tracking-wide text-gray-500 hover:text-gray-800"
                            >
                                <span>Detalle técnico (pruebas)</span>
                                {showTech ? <FaChevronUp /> : <FaChevronDown />}
                            </button>
                            {showTech && (
                                <pre className="text-[11px] leading-relaxed bg-gray-50 border border-gray-200 rounded-xl p-3 overflow-x-auto text-gray-700">
{JSON.stringify(
    {
        productId: itemId,
        cached: payload?.cached,
        geminiKeyChannel: payload?.geminiKeyChannel || result.geminiKeyChannel,
        resultSource: result.resultSource,
        groundingUsed: result.groundingUsed,
        aiModel: result.aiModel,
        latencyMs: result.latencyMs,
        primaryLatencyMs: payload?.primaryLatencyMs ?? result.primaryLatencyMs,
        fallbackLatencyMs: payload?.fallbackLatencyMs ?? result.fallbackLatencyMs,
        usedFallback: payload?.usedFallback ?? result.usedFallback,
        fallbackSkippedReason:
            payload?.fallbackSkippedReason ?? result.fallbackSkippedReason,
        checkId: result.checkId,
        matchedBy: result.matchedBy,
        missingFields: result.missingFields,
        warnings: result.warnings,
        vehicleQuery: payload?.vehicle?.vehicleQuery,
        softParse: {
            brand: payload?.vehicle?.vehicleBrand,
            line: payload?.vehicle?.vehicleLine,
            year: payload?.vehicle?.vehicleModelYear,
        },
        quota: payload?.quota,
        product: payload?.product,
    },
    null,
    2
)}
                                </pre>
                            )}
                        </div>
                    )}
                </div>

                <div className="flex-shrink-0 sticky bottom-0 z-10 border-t border-gray-100 bg-white px-5 py-4 flex flex-col sm:flex-row gap-2">
                    {uiState === 'form' && (
                        <button
                            type="button"
                            disabled={!canSubmit}
                            onClick={handleSubmit}
                            className="w-full sm:flex-1 bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-300 text-white font-bold py-3 rounded-xl transition-colors"
                        >
                            Validar ahora
                        </button>
                    )}

                    {uiState === 'error' && (
                        <button
                            type="button"
                            onClick={() => setUiState('form')}
                            className="w-full sm:flex-1 bg-gray-900 text-white font-bold py-3 rounded-xl"
                        >
                            Volver
                        </button>
                    )}

                    {uiState === 'result' && (
                        <>
                            <button
                                type="button"
                                onClick={() => setUiState('form')}
                                className="w-full sm:flex-1 bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-3 rounded-xl"
                            >
                                Editar vehículo
                            </button>
                            {storeId && (
                                <a
                                    href={storeProductUrl(storeId)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="w-full sm:flex-1 inline-flex items-center justify-center gap-2 bg-gray-900 hover:bg-black text-white font-bold py-3 rounded-xl"
                                >
                                    <FaExternalLinkAlt /> Ver en tienda
                                </a>
                            )}
                            <button
                                type="button"
                                onClick={onClose}
                                className="w-full sm:w-auto px-4 py-3 rounded-xl border border-gray-200 font-semibold text-gray-700"
                            >
                                Cerrar
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );

    return createPortal(modal, document.body);
};

export default CompatibilityCheckModal;
